<script>
import { formatMinskTimestamp } from '@/scripts/tools.js';
import { getStatusConfig } from '@/scripts/corporateRequests.js';

export default {
    props: {
        request: {
            type: Object,
            required: true,
        },
    },
    methods: {
        getStatusBadge(status) {
            return getStatusConfig(status);
        },
        formatRelativeTime(isoString) {
            if (!isoString) return 'N/A';

            const SECONDS_IN_MINUTE = 60;
            const MINUTES_IN_HOUR = 60;
            const HOURS_IN_DAY = 24;
            const DAYS_IN_WEEK = 7;
            const MS_IN_SECOND = 1000;
            const MINSK_OFFSET_MS = 3 * 60 * 60 * 1000; // UTC+3 in milliseconds

            // Backend sends UTC time without 'Z' suffix, so add it to ensure correct parsing
            const utcString = isoString.endsWith('Z')
                ? isoString
                : isoString + 'Z';
            const utcDate = new Date(utcString);
            const utcNow = new Date();

            // Calculate the difference (both are in UTC)
            const diffMs = utcNow - utcDate;
            const diffSecs = Math.floor(diffMs / MS_IN_SECOND);
            const diffMins = Math.floor(diffSecs / SECONDS_IN_MINUTE);
            const diffHours = Math.floor(diffMins / MINUTES_IN_HOUR);
            const diffDays = Math.floor(diffHours / HOURS_IN_DAY);

            if (diffSecs < SECONDS_IN_MINUTE) return 'Just now';
            if (diffMins < MINUTES_IN_HOUR) return `${diffMins}m ago`;
            if (diffHours < HOURS_IN_DAY) return `${diffHours}h ago`;
            if (diffDays < DAYS_IN_WEEK) return `${diffDays}d ago`;

            // Format as DD.MM.YYYY using Minsk time (UTC + 3 hours)
            const minskDate = new Date(utcDate.getTime() + MINSK_OFFSET_MS);
            const day = String(minskDate.getUTCDate()).padStart(2, '0');
            const month = String(minskDate.getUTCMonth() + 1).padStart(2, '0');
            const year = minskDate.getUTCFullYear();
            return `${day}.${month}.${year}`;
        },
        formatFullTimestamp(isoString) {
            return formatMinskTimestamp(isoString);
        },
        viewDetails() {
            this.$store.dispatch('loadRequestDetail', this.request.id);
        },
    },
};
</script>

<template>
    <tr class="request_row" @click="viewDetails">
        <td class="cell_id">{{ request.id.substring(0, 8) }}...</td>
        <td class="cell_company">{{ request.company_name }}</td>
        <td class="cell_contact">{{ request.contact_name }}</td>
        <td class="cell_email">{{ request.email }}</td>
        <td class="cell_licenses cell_center">
            <span class="licenses_badge">{{ request.licenses_count }}</span>
        </td>
        <td class="cell_status cell_center">
            <span :class="getStatusBadge(request.status).cssClass">
                {{ getStatusBadge(request.status).text }}
            </span>
        </td>
        <td class="cell_date" :title="formatFullTimestamp(request.created_at)">
            {{ formatRelativeTime(request.created_at) }}
        </td>
        <td class="cell_actions cell_center">
            <button @click.stop="viewDetails" class="view_button">View</button>
        </td>
    </tr>
</template>

<style scoped>
.request_row {
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.request_row:hover {
    background-color: var(--background-hover);
}

.request_row td {
    padding: 12px 15px;
    border-bottom: 1px solid var(--border-color);
    color: var(--text-color);
}

.cell_id {
    font-family: monospace;
    font-size: 12px;
    color: var(--text-color-secondary);
}

.cell_company {
    font-weight: 600;
}

.cell_email {
    font-family: monospace;
    font-size: 13px;
    color: var(--text-color-secondary);
}

.cell_center {
    text-align: center;
}

.licenses_badge {
    display: inline-block;
    padding: 4px 10px;
    background: #e3f2fd;
    color: #1976d2;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
}

.status_badge {
    display: inline-block;
    padding: 5px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.status_pending {
    background: #fff3cd;
    color: #856404;
    border: 1px solid #ffc107;
}

.status_contacted {
    background: #d1ecf1;
    color: #0c5460;
    border: 1px solid #17a2b8;
}

.status_converted {
    background: #d4edda;
    color: #155724;
    border: 1px solid #28a745;
}

.status_rejected {
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #dc3545;
}

.view_button {
    padding: 6px 16px;
    background: var(--main-color);
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    transition: opacity 0.2s ease;
}

.view_button:hover {
    opacity: 0.9;
}
</style>
